import React from "react";
import ReactDOMServer from "react-dom/server";
import {
    FormatterCallbackFunction,
    Point,
    SeriesTooltipOptionsObject,
    TooltipFormatterContextObject,
    TooltipOptions
} from "highcharts";
import { nonEmptyArray, option, record } from "fp-ts";
import { capitalize } from "lodash";

export const BING_API_KEY = 'Agl-rpGco3Mo07n16sDpY4jsu35RAbvEwPAND7hi8-6JgIFVetQdhnZ4i_oSiNyd'

export const WIDE_SCREEN = 3656;

export const weatherURL = (path: string) =>
    (process.env.REACT_APP_WEATHER_API_URL || '/weatherAPI/') + path

export const dashboardURL = (path: string) =>
    (process.env.REACT_APP_DASHBOARD_API_URL || '/dashboardAPI/') + path

export function monthsForLocale(localeName = 'RU-ru', monthFormat = 'long'): string[] {
    // @ts-ignore
    const format = new Intl.DateTimeFormat(localeName, {month: monthFormat}).format;
    return [...Array(12).keys()].map((m) => format(new Date(Date.UTC(2021, (m)%12))));
}

export const shortMonthNames = monthsForLocale('ru-RU', 'short')
    .map(i => capitalize(i.replaceAll(".", "").substring(0, 3)))


export function getRandomArbitrary(min: number, max: number) {
    return Math.random() * (max - min) + min;
}

export function hexToRgb(hex: string): { r: number, g: number, b: number} {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 };
}

export const groupByHead = <T>(fn:  (a: T) => string) => (as: T[]): { [k: string] : T } => {
    return record.map<nonEmptyArray.NonEmptyArray<T>, T>(nonEmptyArray.head)(nonEmptyArray.groupBy<T>(fn)(as));
}

export const groupByProp = <T extends Record<string, any>>(prop: keyof T) => (as: T[]): { [k: string] : T } => {
     return groupByHead<T>(x => option.getOrElse(() => `key_not_found_${String(prop)}`)(
        option.map(String)(record.lookup(prop as string)(x)))
    )(as);
}

export const groupByProp_ = <T extends Record<string, any>>(prop: keyof T) => (as: T[]): { [k: string] : T[] } => {
    return nonEmptyArray.groupBy<T>(x => option.getOrElse(() => `key_not_found_${String(prop)}`)(
        option.map(String)(record.lookup(prop as string)(x)))
    )(as);
}


export function getRandomInt(min: number, max: number) {
    return Math.floor(getRandomArbitrary(min, max));
}

export function applyTip(element: (ctx: TooltipFormatterContextObject, tooltip: Highcharts.Tooltip) => React.ReactElement, options: TooltipOptions = {}): TooltipOptions {
    return {
        useHTML: true,
        shared: true,
        outside: true,
        style: {opacity: 0},
        formatter: function(tooltip){
            return ReactDOMServer.renderToString(element(this, tooltip))
        },
        ...options
    }
}

export function applyTipWith(element: (ctx: TooltipFormatterContextObject) => React.ReactElement, options: TooltipOptions = {}): TooltipOptions {
    return {
        useHTML: true,
        style: {opacity: 0},
        formatter: function(){
            return ReactDOMServer.renderToString(element(this))
        },
        ...options
    }
}

export function applySeriesTip(element: (ctx: Point) => React.ReactElement, options: SeriesTooltipOptionsObject = {}): SeriesTooltipOptionsObject {
    return {
        pointFormatter: function(p: Point){
            return ReactDOMServer.renderToString(element(p))
        } as FormatterCallbackFunction<Point>,
        ...options
    }
}

export function checkWindowSize<T>(onWide: T, onDefault: T): T {
    return window.innerWidth >= WIDE_SCREEN ? onWide : onDefault
}

export function wideFontSize16() {
    return checkWindowSize('16px', '10px');
}

export function wideFontSize18() {
    return checkWindowSize('18px', '12px');
}

export function wideFontSize20() {
    return checkWindowSize('20px', '14px');
}

export function wideFontSize24() {
    return checkWindowSize('24px', '14px');
}

export function wideFontSize32() {
    return checkWindowSize('32px', '14px');
}